import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { request, unwrap } from '../api/http'

export interface SiteInfo {
  browserName: string
  loginName: string
  appLoginName: string
  appName: string
  techHelp: string
  beianName: string
  version: string
  screenName: string
  siteIcon: string
}

const fallbackName = import.meta.env.VITE_APP_TITLE || '物联网监控平台'
const FALLBACK: SiteInfo = {
  browserName: fallbackName,
  loginName: fallbackName,
  appLoginName: fallbackName,
  appName: fallbackName,
  techHelp: '',
  beianName: '',
  version: '',
  screenName: fallbackName,
  siteIcon: ''
}

function text(value: unknown, fallback = '') {
  const next = String(value ?? '').trim()
  return next || fallback
}

export const useSiteStore = defineStore('site', () => {
  const info = ref<SiteInfo>({ ...FALLBACK })
  const loaded = ref(false)
  let pending: Promise<SiteInfo> | null = null

  async function load(force = false) {
    if (loaded.value && !force) return info.value
    if (pending) return pending
    pending = (async () => {
      try {
        const data = unwrap<Partial<SiteInfo>>(await request({ url: '/auth/site' })) || {}
        const browserName = text(data.browserName, FALLBACK.browserName)
        info.value = {
          browserName,
          loginName: text(data.loginName, browserName),
          appLoginName: text(data.appLoginName, browserName),
          appName: text(data.appName, browserName),
          techHelp: text(data.techHelp),
          beianName: text(data.beianName),
          version: text(data.version),
          screenName: text(data.screenName, browserName),
          siteIcon: text(data.siteIcon)
        }
      } catch {
        if (!loaded.value) info.value = { ...FALLBACK }
      } finally {
        loaded.value = true
        pending = null
      }
      return info.value
    })()
    return pending
  }

  return {
    info,
    loaded,
    load,
    browserName: computed(() => info.value.browserName),
    loginName: computed(() => info.value.loginName),
    appName: computed(() => info.value.appName),
    techHelp: computed(() => info.value.techHelp),
    beianName: computed(() => info.value.beianName),
    version: computed(() => info.value.version),
    screenName: computed(() => info.value.screenName),
    siteIcon: computed(() => info.value.siteIcon)
  }
})
